import { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import axios, { AxiosRequestConfig } from 'axios';
import _ from 'lodash';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';
import * as yup from 'yup';

import { CLASS_CREATE_ZOOM_PASSWORD_ERROR } from './error-codes';
import {
    AdditionalInstructors,
    CanJoinBeforeInstructor,
    ClassData,
    ClassPayload,
    ClassSessionGroupPayload,
    ClassStatus,
    MeetingObj,
    RequireAuth,
    TemplateRecord,
    WaitingRoom
} from './types';
import type { ZoomMeetingSettings } from '../../../../../models/types';
import { ClassSession, ClassSessionWithGroupData } from '../../../components/addSessions/types';
import { useCreateCalendarValues } from '../../../components/addSessions/useCreateCalendarValues';
import { aggregateCalendarDates } from '../../../components/addSessions/utils/aggregateCalendarDates';
import {
    calcSessionsFromClassTimes,
    calcSessionsFromDates,
    filterSessions
} from '../../../components/addSessions/utils/calcSessions';
import { CLASS_CREATE, GET_CLASS_DATA_DATA_PORTAL_PATH, UPDATE_CLASS, USERS } from '../../../config/apiPaths';
import { ClassRecord, ClassSessionGroup, ClassSessionInfo, ClassStudentGroup } from '../../../context/types';
import { useAppMessaging } from '../../../hooks/useAppMessaging';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import { UserRecord } from '../../../types/apiTypes';
import { getUserUuidHeaders } from '../../../utils/httpUtils';
import { isValidEmail } from '../../../utils/inputValidation';
import { PasswordGenerator } from '../../../utils/passwordGenerator';
import { getUrlParams, getUrls, processApiPath } from '../../../utils/url';
import { usePrevious } from '../../../utils/usePrevious';
import AdminAuthContainer from '../AdminAuthContainer';

type UpdateClassCallbacks = { onSuccess?: () => void; onError?: () => void };

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

type FormActionType = 'CREATE' | 'EDIT';

interface ClassContainerState {
    onClose?: () => void;
    onUpdate?: () => void;
    classData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

const useClassContainer = (state: ClassContainerState = {}) => {
    const { api } = getUrls();

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);

    const { closeWindow, sendMessage } = useAppMessaging();

    const { classUuid: classUuidV1 } = useParams<{ classUuid?: string }>();
    const { class_uuid: classUuidV2 } = getUrlParams();
    const classUuid = classUuidV1 || classUuidV2 || '';
    const [formActionType] = useState<FormActionType>(classUuid ? 'EDIT' : 'CREATE');
    const [meetingId, setMeetingId] = useState('');
    const [initialClassTimes, setInitialClassTimes] = useState<string>('');
    const [classRecord, setClassRecord] = useState<ClassRecord>();
    const [meetingSettings, setMeetingSettings] = useState<ZoomMeetingSettings>();
    const [ltiState, setLtiState] = useState<any>({});

    const [instructors, setInstructors] = useState<UserRecord[]>([]);
    const [instructorsStatus, setInstructorsStatus] = useState<LoadingStatus>('INITIAL');

    const [classGroups, setClassGroups] = useState<ClassStudentGroup[]>([]);
    const [originalClassSessionGroups, setOriginalClassSessionGroups] = useState<ClassSessionGroup[]>([]);

    const [createClassStatus, setCreateClassStatus] = useState<LoadingStatus>('INITIAL');
    const [updateClassStatus, setUpdateClassStatus] = useState<LoadingStatus>('INITIAL');
    const [getClassToEditStatus, setGetClassToEditStatus] = useState<LoadingStatus>('INITIAL');
    const [publishedTemplates, setPublishedTemplates] = useState<TemplateRecord[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { data: classSettings } = useOrgSettings();

    const allowForcedAuthentication =
        classSettings?.authSettings.forcedAuthentication || meetingSettings?.meeting_authentication;

    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem('user_uuid');

    const { t } = useTranslation('create_edit_class');
    const { t: tCommon } = useTranslation('common');

    const { schema: calendarSchema, defaultValues: defaultCalendarValues } = useCreateCalendarValues();

    const schema = yup.object({
        className: yup.string().required(t('details.class-name-required')),
        classId: yup.string().optional(),
        description: yup.string().optional(),
        classPasscode: yup.string().required(t('details.class-passcode-required')),
        courseTemplate: yup.boolean().optional(),
        courseTemplateId: yup.string().optional(),
        waitingRoom: yup.mixed<WaitingRoom>().oneOf(Object.values(WaitingRoom)),
        canJoinBeforeInstructor: yup.mixed<CanJoinBeforeInstructor>().oneOf(Object.values(CanJoinBeforeInstructor)),
        requireAuth: yup.mixed<RequireAuth>().oneOf(Object.values(RequireAuth)),
        instructorEmails: yup
            .array()
            .of(yup.string())
            .test({
                test(emails) {
                    if (emails) {
                        const errorEmails: string[] = [];

                        emails.forEach((email) => {
                            if (email && !isValidEmail(email)) {
                                errorEmails.push(email);
                            }
                        });

                        const errorEmailsString = errorEmails.join(', ');

                        if (errorEmails.length) {
                            return this.createError({
                                message: t('advanced.invalid-emails', { errorEmails: errorEmailsString }),
                                path: 'instructorEmails'
                            });
                        }
                    }
                    return true;
                }
            }),
        contentType: yup.string().optional()
    });

    const mergedSchema = schema.concat(yup.object().shape({ dates: yup.array().of(calendarSchema) }));

    const passwordGenerator = new PasswordGenerator();

    const defaultValues = {
        className: '',
        classId: '',
        description: '',
        classPasscode: passwordGenerator.generatePassword({ length: 10 }),
        courseTemplate: false,
        courseTemplateId: '',
        dates: [defaultCalendarValues],
        waitingRoom: WaitingRoom.OFF,
        canJoinBeforeInstructor: CanJoinBeforeInstructor.YES,
        requireAuth: RequireAuth.OFF,
        instructorEmails: [] as string[],
        classSessions: [] as ClassSessionInfo[],
        classSessionGroups: [] as ClassSessionGroup[],
        classGroups: [] as ClassStudentGroup[],
        contentType: 'default',
        ltiState: {}
    };

    const { control, watch, handleSubmit, register, getValues, reset, formState, setValue } = useForm({
        defaultValues,
        resolver: yupResolver(mergedSchema)
    });

    const formErrors = formState.errors;

    const courseTemplateSelected = watch('courseTemplate');
    useEffect(() => {
        if (!courseTemplateSelected) {
            setValue('courseTemplateId', '');
        }
    }, [setValue, courseTemplateSelected]);

    const { append, remove, fields } = useFieldArray({ name: 'dates', control });

    const addDefaultDate = () => {
        append(defaultCalendarValues);
    };

    const removeDate = (index: number) => {
        remove(index);
    };

    const watchDatesArray = watch('dates');

    const dateFields = fields.map((field, index) => ({
        ...field,
        ...watchDatesArray[index]
    }));

    const sessions: ClassSessionWithGroupData[] = useMemo(() => {
        // @NOTE: sessions can be derived from user input in the date fields OR
        // sessions can be derived from what came from the database

        if (formState.dirtyFields?.dates) {
            // active representation of user's intent when filling out date fields
            return calcSessionsFromDates(dateFields);
        }

        if (getClassToEditStatus === 'SUCCESS' && initialClassTimes) {
            // representation of what's in the db
            return calcSessionsFromClassTimes(
                initialClassTimes,
                classRecord?.data?.timezone ?? '',
                originalClassSessionGroups
            );
        }

        return [];
    }, [
        dateFields,
        formState.dirtyFields?.dates,
        getClassToEditStatus,
        classRecord?.data?.timezone,
        initialClassTimes,
        originalClassSessionGroups
    ]);

    const [excludedSessions, setExcludedSessions] = useState<ClassSession[]>([]);

    const prevSessions = usePrevious(sessions);

    useEffect(() => {
        // if sessions are updated, reset excluded sessions
        // this prevents the need for some kind of reset exclusions button, or having to start the form from scratch
        if (!_.isEqual(sessions, prevSessions)) {
            setExcludedSessions([]);
        }
    }, [sessions, prevSessions]);

    const setValuesFromClassData = useCallback(
        (data) => {
            const timezone = data?.class?.data?.timezone ?? '';
            const classTimes = data?.class?.data?.class_times ?? '';
            const dates = aggregateCalendarDates({
                classTimes,
                timezone,
                classSessionGroups: data?.classSessionGroups ?? []
            });

            reset({
                className: data?.class?.class_name ?? '',
                classId: data?.class?.class_code ?? '',
                description: data?.class.class_description ?? '',
                classPasscode: data?.meeting?.password ?? '',
                waitingRoom: data?.meeting?.settings?.waiting_room ? WaitingRoom.ON : WaitingRoom.OFF,
                canJoinBeforeInstructor:
                    data?.meeting?.settings?.join_before_host === false
                        ? CanJoinBeforeInstructor.NO
                        : CanJoinBeforeInstructor.YES,
                requireAuth: data?.meeting?.settings?.meeting_authentication ? RequireAuth.ON : RequireAuth.OFF,
                dates,
                classSessions: data?.classSessions ?? [],
                classSessionGroups: data?.classSessionGroups ?? [],
                classGroups: data?.classGroups ?? [],
                contentType: data?.class?.content_type ?? 'default'
            });
        },
        [reset]
    );

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            const errorCode = error?.response?.data?.errorCode;
            const zoomPasswordError = error?.response?.data?.errorData;

            if (
                errorCode === CLASS_CREATE_ZOOM_PASSWORD_ERROR &&
                zoomPasswordError &&
                typeof zoomPasswordError === 'string'
            ) {
                return zoomPasswordError;
            }

            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getPublishedTemplates = useCallback(async (): Promise<any> => {
        if (!userUuid) return;

        try {
            const config: AxiosRequestConfig = {
                headers: getUserUuidHeaders(userUuid),
                params: { status: ClassStatus.Published }
            };

            const { data } = await axios.get<TemplateRecord[]>(`${api}/v1/templates/template-get`, config);

            setPublishedTemplates(data);
        } catch (err) {
            console.error('unable to get published templates');
        }
    }, [api, userUuid]);

    useEffect(() => {
        if (publishedTemplates.length === 0 && formActionType === 'CREATE') {
            getPublishedTemplates();
        }
    }, [formActionType, getPublishedTemplates, publishedTemplates.length]);

    const getUsers = useCallback(async () => {
        setInstructorsStatus('LOADING');

        try {
            const { data } = await axios.get<UserRecord[]>(`${api}${USERS}?user_uuid=${userUuid}&role=teacher`);

            const cleanData: UserRecord[] = data.map((user) => ({
                id: user.id,
                user_name: user.user_name ?? '',
                email: user.email ?? ''
            }));

            cleanData.sort((a, b) => {
                const aTrimmed = a.user_name.trim();
                const bTrimmed = b.user_name.trim();

                return aTrimmed.localeCompare(bTrimmed);
            });

            setInstructors(cleanData ?? []);

            setInstructorsStatus('SUCCESS');
        } catch (err) {
            setInstructorsStatus('ERROR');
        }
    }, [api, userUuid]);

    useEffect(() => {
        if (instructors.length === 0) {
            getUsers();
        }
    }, [instructors.length, getUsers]);

    const getClassDataCall = useCallback(async () => {
        const { data } = await axios.get(
            processApiPath(GET_CLASS_DATA_DATA_PORTAL_PATH, undefined, {
                class_uuid: classUuid,
                with_info: 'true',
                auth_mode: authMode,
                demo_content: String(showDemoContent)
            }),
            { headers: getUserUuidHeaders(userUuid as string) }
        );

        if (data?.success === false) {
            throw Error(data?.error);
        }
        if (data && data.classSessions && data.classSessionGroups) {
            const classSessionGroupsByUuid = {};
            data.classSessionGroups.forEach((classSessionGroup) => {
                classSessionGroupsByUuid[classSessionGroup.class_session_group_uuid] = classSessionGroup;
            });
            data.classSessions.forEach((classSession) => {
                if (classSession.class_session_group_uuid) {
                    classSession.class_session_group = classSessionGroupsByUuid[classSession.class_session_group_uuid];
                }
            });
        }
        return data;
    }, [userUuid, classUuid, authMode, showDemoContent]);

    const getClass = useCallback(async () => {
        setGetClassToEditStatus('LOADING');

        try {
            const data = state.classData ?? (await getClassDataCall());

            setInitialClassTimes(data?.class?.data?.class_times ?? '');
            setValuesFromClassData(data);
            setClassRecord(data.class);
            setMeetingSettings(data?.meeting?.settings);

            setClassGroups(data.classGroups);
            setOriginalClassSessionGroups(data.classSessionGroups);

            setMeetingId(data?.class?.meeting_id);
            setLtiState(data?.class?.lti_state);

            setGetClassToEditStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetClassToEditStatus('ERROR');
        }
    }, [state.classData, getClassDataCall, setValuesFromClassData, returnErrorMessage]);

    useEffect(() => {
        // Edit existing class
        if (classUuid && userUuid) {
            getClass();
        }
    }, [classUuid, getClass, userUuid]);

    const excludeSession = (session: ClassSession) => {
        setExcludedSessions((previous) => [...previous, session]);
    };

    const returnPayloadFromFormValues = useCallback(() => {
        const {
            className,
            classId,
            description,
            dates,
            classPasscode,
            canJoinBeforeInstructor,
            waitingRoom,
            requireAuth,
            courseTemplate,
            courseTemplateId,
            instructorEmails,
            contentType
        } = getValues();
        const filteredSessions = filterSessions({ sessions, excludedSessions });
        const startAndEnd = filteredSessions.map((session) => `${session.start_time}:${session.end_time}`);

        const instructorsPayload: AdditionalInstructors[] = [];
        if (instructorEmails) {
            instructorEmails.forEach((instructorEmail) => {
                const found = instructors.find((instructor) => instructor.email === instructorEmail);

                if (found) {
                    instructorsPayload.push({ email: found.email, user_name: found.user_name });
                } else {
                    instructorsPayload.push({ email: instructorEmail, user_name: instructorEmail });
                }
            });
        }

        const meetingObj: MeetingObj = {
            topic: className,
            password: classPasscode,
            agenda: description,
            settings: {
                join_before_host: canJoinBeforeInstructor === CanJoinBeforeInstructor.YES,
                waiting_room: waitingRoom === WaitingRoom.ON,
                meeting_authentication: requireAuth === RequireAuth.ON
            }
        };

        if (formActionType === 'EDIT') {
            meetingObj.meeting_id = meetingId;
        }

        const classSessionGroupsPayload: any = {};
        _.each(sessions, (session) => {
            if (session.classSessionGroupUuid) {
                if (!classSessionGroupsPayload[session.classSessionGroupUuid]) {
                    classSessionGroupsPayload[session.classSessionGroupUuid] = {
                        class_session_group_uuid: session.classSessionGroupUuid,
                        schedule: {
                            class_times: []
                        },
                        config: {
                            label: session.label,
                            selectedGroups: session.selectedGroups,
                            attendanceType: session.attendanceType
                        }
                    } as ClassSessionGroupPayload;
                }
                classSessionGroupsPayload[session.classSessionGroupUuid].schedule.class_times.push(
                    `${session.start_time}:${session.end_time}`
                );
            }
        });
        const classSessionGroupsPayloadArray: ClassSessionGroupPayload[] = _.values(
            classSessionGroupsPayload
        ) as ClassSessionGroupPayload[];

        const classDataNew: ClassData = {
            class_name: className,
            class_description: description,
            class_code: classId,
            content_type: contentType
        };

        if (courseTemplate && courseTemplateId) {
            classDataNew.template_id = courseTemplateId;
        }

        if (dates.length) {
            classDataNew.timezone = dates[0]?.timezone ?? null;
            classDataNew.class_times = startAndEnd.join(',');
        }

        if (dates.length) {
            classDataNew.timezone = dates[0]?.timezone ?? null;
            classDataNew.class_times = startAndEnd.join(',');
        }

        const payload: ClassPayload = {
            meeting_obj: meetingObj,
            class_data: classDataNew,
            user_uuid: userUuid,
            class_session_groups: classSessionGroupsPayloadArray
        };

        if (formActionType === 'CREATE') {
            payload.additional_instructors = instructorsPayload;
        }

        return payload;
    }, [excludedSessions, formActionType, getValues, instructors, meetingId, sessions, userUuid]);

    const onClose = useCallback(() => {
        state.onClose?.();
        closeWindow();
    }, [closeWindow, state]);

    const handleSuccess = useCallback(() => {
        sendMessage({
            func: 'call_js_on_labeled_window',
            data: { func: 'refresh', label: 'login_window' }
        });

        sendMessage({ func: 'refresh_parent_window' });

        state.onUpdate?.();
        onClose();
    }, [sendMessage, state, onClose]);

    const onCancel = () => {
        onClose();
    };

    const createClass = async () => {
        setCreateClassStatus('LOADING');

        try {
            const payload = returnPayloadFromFormValues();
            await axios.post(`${api}${CLASS_CREATE}`, payload);

            setCreateClassStatus('SUCCESS');

            handleSuccess();
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setCreateClassStatus('ERROR');
        }
    };

    const updateClass = useCallback(
        async ({ onSuccess, onError }: UpdateClassCallbacks = {}) => {
            setUpdateClassStatus('LOADING');

            try {
                const payload = returnPayloadFromFormValues();
                await axios.put(`${api}${UPDATE_CLASS}`, { ...payload });

                setUpdateClassStatus('SUCCESS');

                if (onSuccess) {
                    onSuccess();
                } else {
                    handleSuccess();
                }
            } catch (err) {
                const errorMsg = returnErrorMessage(err);
                setErrorMessage(errorMsg);
                setUpdateClassStatus('ERROR');
                if (onError) {
                    onError();
                }
            }
        },
        [api, handleSuccess, returnErrorMessage, returnPayloadFromFormValues]
    );

    return {
        allowForcedAuthentication,

        excludedSessions,
        excludeSession,

        handleSubmit,
        formActionType,
        register,
        addDefaultDate,
        createClass,
        createClassStatus,

        updateClass,
        updateClassStatus,
        classRecord,

        classGroups,

        publishedTemplates,

        instructorsStatus,
        instructors,

        getClassToEditStatus,
        removeDate,
        sessions,
        control,
        reset,
        errorMessage,
        onCancel,
        dateFields,
        setValue,
        watch,

        ltiState,
        formErrors
    };
};

const ClassContainer = createContainer(useClassContainer);

export default ClassContainer;
